import {
  Drawer,
  DrawerOverlay,
  DrawerCloseButton,
  DrawerHeader,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useDisclosure,
  TabIndicator,
  Stack,
  Spinner,
  Text,
  Button,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import SelectButton from "./Buttons/SelectButton";
import { CourseTopic, CourseLevel } from "@shared_types";
import { BsFilter } from "react-icons/bs";
import { useCourseFilters } from "@/hooks/useCourseFilters";
import { Filters } from "@/api/Api";

interface FiltersDrawerProps {
  value: number | null;
  selectedTopics?: number[];
  onFiltersChange?: (filters: Filters) => void;
  className?: string;
  onTabChange?: (tabIndex: number) => void;
  onClearFilters?: () => void;
}

export default function FiltersDrawer({
  value,
  selectedTopics = [],
  onFiltersChange,
  onTabChange,
  onClearFilters,
}: FiltersDrawerProps) {
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [tempValue, setTempValue] = React.useState<number | null>(value);
  const [tempSelectedTopics, setTempSelectedTopics] =
    React.useState<number[]>(selectedTopics);

  const {
    topicsData,
    levelsData,
    topicsLoading,
    levelsLoading,
    topicsError,
    levelsError,
  } = useCourseFilters();

  useEffect(() => {
    setTempValue(value);
  }, [value, isOpen]);

  useEffect(() => {
    setTempSelectedTopics(selectedTopics);
  }, [selectedTopics, isOpen]);

  const handleApply = () => {
    if (onFiltersChange) {
      onFiltersChange({
        selectedLevel: tempValue,
        selectedTopics: tempSelectedTopics,
      });
    }
    onClose();
  };

  const handleClearAll = () => {
    setTempValue(null);
    setTempSelectedTopics([]);
    onClearFilters?.();
  };

  const handleTabChange = (index: number) => {
    onTabChange?.(index);
  };

  const handleRadioChange = (val: string) => {
    const numVal = Number(val);
    setTempValue(tempValue === numVal ? null : numVal);
  };

  const handleCheckboxChange = (val: string) => {
    const numVal = Number(val);
    if (numVal === 0) {
      // "All Topics" option
      setTempSelectedTopics([]);
    } else {
      // Toggle the selected topic
      if (tempSelectedTopics.includes(numVal)) {
        setTempSelectedTopics(tempSelectedTopics.filter((id) => id !== numVal));
      } else {
        setTempSelectedTopics([...tempSelectedTopics, numVal]);
      }
    }
  };

  const drawerContentWidth = "600px";
  return (
    <>
      <Button
        leftIcon={<BsFilter color={"var(--color-grey)"} size={18} />}
        ref={btnRef}
        variant={"outline"}
        border={"1px solid var(--color-grey-light)"}
        px={2}
        rounded={"xl"}
        size={"sm"}
        onClick={onOpen}
        fontSize={"var(--base-font-size-small)"}
        aria-label="Filters"
      >
        Levels
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="bottom"
        onClose={onClose}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent roundedTop={"xl"} height={"60svh"}>
          <DrawerCloseButton />
          <DrawerHeader
            fontSize={"var(--base-font-size-xlarge)"}
            pb={2}
            boxSizing={"content-box"}
            maxWidth={drawerContentWidth}
            textAlign={"left"}
            w={"100%"}
            margin={"0 auto"}
          >
            Filters
          </DrawerHeader>

          <DrawerBody py={0}>
            <Tabs
              position="relative"
              variant="unstyled"
              size={"sm"}
              maxWidth={drawerContentWidth}
              boxSizing={"content-box"}
              margin={"0 auto"}
              onChange={handleTabChange}
            >
              <TabList>
                <Tab>Level</Tab>
                <Tab>Topic</Tab>
              </TabList>
              <TabIndicator
                mt="0px"
                height="6px"
                rounded="full"
                bg="var(--color-orange)"
                width="3px"
              />
              <TabPanels>
                <TabPanel pb={2}>
                  {levelsLoading ? (
                    <Spinner />
                  ) : levelsError ? (
                    <Text color="var(--color-red)">Error loading levels</Text>
                  ) : (
                    <Stack direction="column" gap={2}>
                      <SelectButton
                        value="0"
                        isSelected={tempValue === 0}
                        onChange={handleRadioChange}
                      >
                        All Levels
                      </SelectButton>
                      {levelsData?.levels?.map((level: CourseLevel) => (
                        <SelectButton
                          key={level.id}
                          value={level.id.toString()}
                          isSelected={tempValue === level.id}
                          onChange={handleRadioChange}
                        >
                          {level.name}
                        </SelectButton>
                      ))}
                    </Stack>
                  )}
                </TabPanel>
                <TabPanel pb={2}>
                  {topicsLoading ? (
                    <Spinner />
                  ) : topicsError ? (
                    <Text color="var(--color-red)">Error loading topics</Text>
                  ) : (
                    <Stack direction="column" gap={2}>
                      <SelectButton
                        value="0"
                        isSelected={tempSelectedTopics.length === 0}
                        onChange={handleCheckboxChange}
                      >
                        All Topics
                      </SelectButton>
                      {topicsData?.topics?.map((topic: CourseTopic) => (
                        <SelectButton
                          key={topic.id}
                          value={topic.id.toString()}
                          isSelected={tempSelectedTopics.includes(topic.id)}
                          onChange={handleCheckboxChange}
                        >
                          <span>{topic.emoji}</span>
                          <span className="ml-2">{topic.name}</span>
                        </SelectButton>
                      ))}
                    </Stack>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </DrawerBody>

          <DrawerFooter
            gap={2}
            margin={"0 auto"}
            w={"100%"}
            pb={5}
            maxWidth={drawerContentWidth}
          >
            <Button
              fontSize={"var(--base-font-size-xs)"}
              rounded={"xl"}
              height={"calc(var(--button-height) - 8px)"}
              variant="outline"
              fontWeight={"medium"}
              p={2}
              w={"50%"}
              border={"1px solid var(--color-grey-light)"}
              onClick={handleClearAll}
            >
              Clear All
            </Button>
            <Button
              fontSize={"var(--base-font-size-xs)"}
              rounded={"xl"}
              p={2}
              bg={"var(--color-blue)"}
              color={"white"}
              w={"50%"}
              height={"calc(var(--button-height) - 8px)"}
              onClick={handleApply}
            >
              Apply
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
